import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CardMedia from "@mui/material/CardMedia";
import Chip from "@mui/material/Chip";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { ContainerItemResponse } from "../../shared/api/requests";
import { requests } from "../../shared/api";
import { log } from "../../logger";
import type { ContainerItem, Item } from "@egalregal/shared";
import type { CodeDecoder, ScanFormat } from "./scan";
import {
  createResolveDeps,
  manualFallbackOf,
  outcomeForResolution,
  resolveScan,
} from "./resolution";
import { useScanHistoryEntry } from "./useScanHistoryEntry";
import { StagedPhotoStrip } from "../capture/StagedPhotoStrip";
import { itemCategoryLabel } from "../../shared/components/CategoryChip";
import { CategoryAutocomplete } from "../../shared/components/CategoryAutocomplete";
import type { StagedCapture } from "../capture/CaptureToolbar";
import { BarcodeIcon } from "../../shared/components/icons";
import { useT } from "../../shared/i18n";
import { getMirroredPlacements } from "../search/catalogStore";
import { placementFailureNotice } from "../items/placementNotice";
import { addBarcode, type PlacementOutcome } from "../items/placements";
import { usePlacementsStore } from "../items/placementsStore";
import { resolveMirroredItem } from "./resolveItem";
import {
  DatePropertyFields,
  datePropertiesOf,
  type DatePropertyValues,
} from "../../shared/components/DatePropertyFields";
import { useQrScanner } from "./useQrScanner";
import { ScanChip } from "./ScanChip";
import { ScanFallback } from "./ScanFallback";

export interface ContainerAddPresetProps {
  containerId: string;
  onItemAdded?: (item: ContainerItemResponse) => void;
  onClose: () => void;
  decoder?: CodeDecoder;
}

type Phase = "scan" | "looking" | "preview" | "manual" | "adding";

/**
 * `container-add` preset (scanner-surface 02): the container-scoped scan loop.
 *
 * Continuous decode; a known item previews an absolute-total quantity stepper
 * and writes into this fixed container, an unknown 1D barcode runs the provider
 * lookup and creates-and-places, and an unknown plain-UUID label skips the
 * provider. The camera is hidden with CSS but stays mounted across the
 * scan → preview → scan loop, so confirming an add never restarts it.
 */
export function ContainerAddPreset({
  containerId,
  onItemAdded,
  onClose,
  decoder,
}: ContainerAddPresetProps) {
  const t = useT();
  const { result, format, cameraError, videoRef, scanning } = useQrScanner(decoder, true, "both");
  // Every placement write goes through the ledger (placement-ledger 03): the
  // additive `add` for copies and the absolute `setTotal` for a correction.
  const addPlacement = usePlacementsStore((state) => state.add);
  const setPlacementTotal = usePlacementsStore((state) => state.setTotal);
  const [phase, setPhase] = useState<Phase>("scan");
  const [barcode, setBarcode] = useState("");
  const [known, setKnown] = useState<Item | null>(null);
  const [placementHere, setPlacementHere] = useState<ContainerItem | null>(null);
  const [external, setExternal] = useState<{ name: string; category: string; photo: string | null; metadata: Record<string, unknown> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [manualName, setManualName] = useState("");
  const [manualCategory, setManualCategory] = useState("other");
  const [staged, setStaged] = useState<StagedCapture[]>([]);
  const [manualDates, setManualDates] = useState<DatePropertyValues>({});
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const scanCounts = useRef(new Map<string, number>());
  const handled = useRef<string | null>(null);
  // The container-scoped lookup is the only resolver dep this preset needs
  // (scan-resolution 03); `containerId` selects it for every code.
  const resolverDeps = useMemo(() => createResolveDeps(), []);
  // Record-before/outcome-after seam (results-04): the code in the loop keeps
  // its entry so a confirmed add can mark the created item.
  const history = useScanHistoryEntry();

  const resetForNext = () => {
    setPhase("scan");
    setBarcode("");
    setKnown(null);
    setPlacementHere(null);
    setExternal(null);
    setError(null);
    setQuantity(1);
    setManualName("");
    setManualCategory("other");
    setManualDates({});
    setStaged([]);
    setJustAdded(null);
  };

  const uploadStaged = async (itemId: string) => {
    for (const capture of staged) {
      try {
        await requests.items.addPhoto(itemId, capture.file, "general", capture.location);
      } catch (err) {
        log.warn({ err, itemId }, "Failed to upload staged item photo");
        setError(t("itemAddPhotoFailed"));
      }
    }
  };

  /**
   * Finish one confirmed add. The ledger already wrote the placement mirror, so
   * the callback and the `addedAt` come from that record rather than a parent
   * refresh key; the success snackbar fires only after a saved write. A
   * barcode-first add can complete without a client item (the mirror lookup
   * failed), in which case the loop still finishes without staging photos.
   */
  const handleAddSuccess = async (item: Item | undefined, quantity: number, label: string) => {
    if (item) {
      await uploadStaged(item.id);
      const placement = getMirroredPlacements().find(
        (entry) => entry.itemId === item.id && entry.containerId === containerId,
      );
      onItemAdded?.({
        containerId,
        itemId: item.id,
        quantity,
        addedAt: placement?.addedAt ?? new Date().toISOString(),
        item,
      });
    }
    resetForNext();
    setJustAdded(label);
    setPhase("scan");
  };

  const doLookup = async (code: string, scanFormat: ScanFormat = "manual") => {
    const trimmed = code.trim();
    if (!trimmed) return;
    // Every decode and typed lookup lands in the per-user history (results-04).
    const entry = history.record(trimmed, scanFormat);
    // A plain-UUID QR label may be an item already tagged and placed here: the
    // container-scoped lookup keeps the provider step off for labels, so a
    // known tag still gets the preview/quantity loop and a fresh one falls
    // through to create-and-place. Non-label codes keep the full lookup path
    // (qr-item-tags).
    const { payload } = entry;
    const barcodeValue = entry.code;
    if (!barcodeValue) return;
    setBarcode(barcodeValue);
    setPhase("looking");
    setError(null);
    const seen = scanCounts.current.get(barcodeValue) ?? 0;
    scanCounts.current.set(barcodeValue, seen + 1);
    try {
      const result = await resolveScan(payload, resolverDeps, { containerId });
      entry.outcome(outcomeForResolution(result));
      const fallback = manualFallbackOf(result);
      // An unreachable lookup keeps the old "try again" error and the scan
      // view; a miss, an unknown label or a non-barcode payload is a manual
      // create-and-place.
      if (fallback === "offline") {
        log.warn({ containerId, barcode: barcodeValue }, "Container lookup offline");
        setError(t("scanToAddLookupFailed"));
        setPhase("scan");
        return;
      }
      if (fallback !== null) {
        setKnown(null);
        setPlacementHere(null);
        setExternal(null);
        setQuantity(1);
        setPhase("manual");
        return;
      }
      if (result.kind === "item") {
        setKnown(result.item);
        setPlacementHere(result.placementHere ?? null);
        setExternal(null);
        const current = result.placementHere?.quantity ?? 0;
        // First scan of a placed item agrees with the "already in this
        // container" line; a rescan during the loop offers one more copy.
        setQuantity(seen > 0 ? current + 1 : Math.max(current, 1));
        setPhase("preview");
        return;
      }
      if (result.kind === "product" && result.preview) {
        setKnown(null);
        setPlacementHere(null);
        setExternal(result.preview);
        setQuantity(1);
        setPhase("preview");
        return;
      }
      // A container-scoped lookup only answers with an item or a preview; never
      // leave the loop on "looking" for an impossible result.
      setPhase("manual");
    } catch (err) {
      // An unexpected answer is not a domain result (the resolver rethrows it):
      // the preset reports the failure with its own phase.
      log.warn({ err, containerId }, `Lookup failed for barcode ${trimmed}`);
      setError(t("scanToAddLookupFailed"));
      setPhase("scan");
    }
  };

  // The old panel fired once per newly decoded value and the dialog ignored
  // decodes outside the scan phase. Keep both guards so resetting to "scan"
  // with the same value on screen does not loop, while the next code is
  // accepted without remounting the camera.
  useEffect(() => {
    if (!result || handled.current === result) return;
    handled.current = result;
    if (phase === "scan") void doLookup(result, format ?? "unknown");
  }, [result, phase]);

  const handleManualLookup = async () => {
    await doLookup(barcode, "manual");
  };

  const confirmKnown = async () => {
    if (!known) return;
    setPhase("adding");
    // The stepper is a new total; the ledger's add is additive. Add the
    // difference, correct a lowered total with the absolute set, and treat an
    // unchanged total as nothing to write (the confirm still completes).
    const current = placementHere?.quantity ?? 0;
    const amount = quantity - current;
    let outcome: PlacementOutcome;
    if (amount === 0) outcome = { status: "saved", quantity: current };
    else if (amount > 0) outcome = await addPlacement(containerId, known, amount);
    else outcome = await setPlacementTotal(containerId, known, quantity);

    if (outcome.status !== "saved") {
      // A write that never reached the server is not queued (ADR-0005): the
      // preview keeps its total and the shared notice says why.
      log.warn({ outcome, containerId, itemId: known.id }, "Known item add failed");
      setError(placementFailureNotice(t, outcome));
      setPhase("preview");
      return;
    }
    await handleAddSuccess(known, outcome.quantity, known.name);
  };

  const confirmExternal = async () => {
    if (!external) return;
    setPhase("adding");
    // The container-scoped POST is the one path that resolves or creates the
    // item server-side, so a first-time barcode keeps its provider work —
    // including the local photo download (placement-ledger 03 follow-up). The
    // ledger writes the mirror from the item the server returns.
    const outcome = await addBarcode(containerId, barcode, quantity);
    if (outcome.status !== "saved") {
      log.warn({ outcome, containerId, barcode }, "External item add failed");
      setError(placementFailureNotice(t, outcome));
      setPhase("preview");
      return;
    }
    // The saved write mirrored the returned item; the staging upload and the
    // history outcome need its id.
    const item = resolveMirroredItem(barcode);
    if (!item) {
      log.warn(
        { containerId, barcode },
        "External item missing from the mirror after a saved add",
      );
    } else {
      history.outcome({
        kind: "created-item",
        id: item.id,
        label: item.name,
      });
    }
    await handleAddSuccess(item, outcome.quantity, external.name);
  };

  const confirmManual = async () => {
    if (!manualName.trim()) return;
    setPhase("adding");
    try {
      const item = await requests.items.create({
        barcode,
        name: manualName.trim(),
        category: manualCategory,
        properties: datePropertiesOf(manualDates),
      });
      const outcome = await addPlacement(containerId, item, quantity);
      if (outcome.status !== "saved") {
        log.warn({ outcome, containerId, barcode }, "Manual item add failed");
        setError(placementFailureNotice(t, outcome));
        setPhase("manual");
        return;
      }
      history.outcome({
        kind: "created-item",
        id: item.id,
        label: item.name,
      });
      await handleAddSuccess(item, outcome.quantity, item.name);
    } catch (err) {
      log.warn({ err, containerId, barcode }, "Failed to create and add manual item");
      setError(t("scanToAddFailed"));
      setPhase("manual");
    }
  };

  const name = known?.name ?? external?.name ?? "";
  const category = known
    ? itemCategoryLabel(known.category, t)
    : external
      ? itemCategoryLabel(external.category, t)
      : t("category_other");
  const photo = known
    ? known.photos[0]
      ? requests.media.itemPhotoPreviewUrl(known.id, known.photos[0].id)
      : null
    : external?.photo ?? null;

  return (
    <>
      <DialogTitle>{t("scanToAddTitle")}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: phase === "scan" ? "block" : "none" }}>
          <ScanChip
            videoRef={videoRef}
            scanning={scanning}
            cameraError={cameraError}
            ariaLabel={t("scanToAddTitle")}
            cameraErrorText={t("scanCameraErrorBarcode")}
          />
          <ScanFallback
            value={barcode}
            onChange={setBarcode}
            onSubmit={() => void handleManualLookup()}
            placeholder={t("scanToAddBarcodePlaceholder")}
            submitLabel={t("scanToAddLookup")}
            sx={{ mt: 2 }}
          />
        </Box>

        {phase === "looking" && <LinearProgress sx={{ my: 2 }} />}
        {phase === "adding" && <LinearProgress sx={{ my: 2 }} />}

        {phase === "preview" && (
          <Stack spacing={2} alignItems="center">
            {photo ? (
              <CardMedia
                component="img"
                image={photo}
                alt={name}
                sx={{ width: 120, height: 120, borderRadius: 2, objectFit: "cover" }}
              />
            ) : (
              <Box sx={{ width: 120, height: 120, borderRadius: 2, bgcolor: "grey.200", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <BarcodeIcon fontSize="large" color="disabled" />
              </Box>
            )}
            <Typography variant="h6">{name}</Typography>
            <Chip label={category} size="small" />
            <Typography variant="caption" color="text.secondary">
              {barcode}
            </Typography>
            {known && (
              <Typography variant="body2" color="text.secondary">
                {t("scanToAddAlreadyHere", { count: String(placementHere?.quantity ?? 0) })}
              </Typography>
            )}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button aria-label={t("decrementQuantity")} onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                -
              </Button>
              <Stack alignItems="center" spacing={0}>
                <Typography>{quantity}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("scanToAddNewTotal")}
                </Typography>
              </Stack>
              <Button aria-label={t("incrementQuantity")} onClick={() => setQuantity(quantity + 1)}>
                +
              </Button>
            </Stack>
            {!known && <StagedPhotoStrip captures={staged} onChange={setStaged} />}
            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Stack>
        )}

        {phase === "manual" && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t("scanToAddUnknown", { barcode })}
            </Typography>
            <TextField
              fullWidth
              size="small"
              label={t("scanToAddNameLabel")}
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
            />
            <CategoryAutocomplete
              label={t("filterType")}
              value={manualCategory}
              onChange={setManualCategory}
            />
            <Typography variant="overline" color="text.secondary">
              {t("itemDates")}
            </Typography>
            <DatePropertyFields values={manualDates} onChange={setManualDates} />
            <StagedPhotoStrip captures={staged} onChange={setStaged} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{phase === "scan" ? t("scanToAddDone") : t("cancel")}</Button>
        {phase === "preview" && (
          <Button
            variant="contained"
            onClick={() => void (known ? confirmKnown() : confirmExternal())}
          >
            {t("scanToAddConfirm")}
          </Button>
        )}
        {phase === "manual" && (
          <Button
            variant="contained"
            disabled={!manualName.trim() || !manualCategory.trim()}
            onClick={() => void confirmManual()}
          >
            {t("scanToAddConfirm")}
          </Button>
        )}
      </DialogActions>
      <Snackbar
        open={justAdded !== null}
        autoHideDuration={2500}
        onClose={() => setJustAdded(null)}
        message={justAdded ? t("scanToAddAdded", { name: justAdded }) : ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
